import React, { useCallback, useContext, useMemo } from "react";
import { MobergColumn, MobergFontFamily, MobergFontSize } from "../../../../../../../../../Moberg";
import { useEventReviewProvider } from "../../../../../../../../../Providers/EventReviewProvider";
import { useEBOOSTEventReviewProvider } from "../../../../../../../../../Providers/EBOOSTEventReviewProvider";
import { DimensionsContext } from "../../../../../../../../../Providers/DimensionsProvider";
import { DurationUnit, fromMilliseconds, getFormattedDuration } from "../../../../../Hooks/useDuration";
import { useRecoilValue } from "recoil";
import { currentPatientFileInfoAtom } from "../../../../../Atoms/PatientFile";
// @ts-ignore
import ModernMUITable from "../../../../../../../../../Components/ModernMUITable";

interface SeeAllEventsWindowProps {
    style?: React.CSSProperties
}

type AnnotationTableRow = {
	id: number
	start_time: number
	duration: string
}

export const EventReviewSeeAllEventsWindow: React.FC<SeeAllEventsWindowProps> = () => {
    const eventReviewProvider = useEventReviewProvider()
    const EBOOSTEventReviewProvider = useEBOOSTEventReviewProvider()
    const { height } = useContext(DimensionsContext) 
    const sortedAnnotations = eventReviewProvider.sortedAnnotations
    const tableAnnotations = eventReviewProvider.showIncompleteEventsOnly ? EBOOSTEventReviewProvider.getIncompleteEvents() : sortedAnnotations
    const patientInfo = useRecoilValue(currentPatientFileInfoAtom)
    
    const dateFormatter = useMemo(() => {
        const options: Intl.DateTimeFormatOptions & { fractionalSecondDigits: number, hourCycle: string } = {
            timeZone: patientInfo.timeZone,
            year: "numeric",
            month: "short",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            fractionalSecondDigits: 3,
            hourCycle: "h23"
        }
    
        return new Intl.DateTimeFormat("en-us", options)
    }, [patientInfo.timeZone])

    const handleSelectedRow = useCallback((selectedRow: AnnotationTableRow) => {
        
        const annotation = tableAnnotations[selectedRow.id]

        if (!annotation) {
            return
        }

        EBOOSTEventReviewProvider.navigateToAnnotation(annotation)
    }, [EBOOSTEventReviewProvider, tableAnnotations])

    const annotationTableColumns = useMemo(() => [
        {
            field: "time", flex: 0.6, headerName: "Time", visible: true, 
            minWidth: 120, maxWidth: 250,
            renderCell: (params: any) => {
                const [monthAndDay, year, time] = dateFormatter.format(params.row.time).split(", ")
                return (
                    <MobergColumn style={{ fontSize: MobergFontSize.SMALL }}>
                        <div>{monthAndDay}, {year} {time}</div>
                    </MobergColumn>
                )
            },
        },
        {
            field: "name", flex: 0.8, headerName: "Name", visible: true, 
            minWidth: 100, maxWidth: 300,
            renderCell: (params: any) => {
                return (
                    <span onMouseDown={() => handleSelectedRow(params.row)} style={{ color: "var(--color-accent-interactive)", cursor: "pointer", fontWeight: "bold", textDecoration: "underline" }}>
                        {params.row.name}
                    </span>
                )
            },
        },
        {
            field: "duration", flex: 0.3, headerName: "Duration", visible: true, 
            minWidth: 80, maxWidth: 120,
            renderCell: (params: any) => {
                const duration = fromMilliseconds(params.row.duration, [DurationUnit.SECONDS, DurationUnit.MINUTES])
                return (<span>{getFormattedDuration(duration, { abbreviation: true })}</span>)
            },
        },
        {
            field: "complete", flex: 0.3, headerName: "Complete", visible: true, 
            minWidth: 80, maxWidth: 120,
            renderCell: (params: any) => {
                return (<span>{params.row.complete ? "Yes" : "No"}</span>)
            },
        }
    ], [dateFormatter, handleSelectedRow])

    const annotationTableData = tableAnnotations.map((annotation, index) => ({
        id: index, 
        name: annotation.text,
        time: annotation.start_time,
        duration: annotation.end_time - annotation.start_time,
        complete: EBOOSTEventReviewProvider.reviewedEventIds.has(annotation.id)
    }))

    return (
        <MobergColumn verticalAlign="start" style={{ fontFamily: MobergFontFamily.REGULAR, padding: "4px", minWidth: "600px", height: height, borderRight: "1px solid var(--color-border-default)" }}>
            <ModernMUITable
                tableData={annotationTableData}
                columns={annotationTableColumns}
                checkboxSelection={false}
                showToolbar={false}
                height={height - 4}
                handleOnRowClick={handleSelectedRow}
                dense={true}
                overrideStyles={{
                    background: "var(--color-surface-panel)",
                    color: "var(--color-text-primary)",
                    width: "575px",
                    position: "absolute",
                    "& .MuiDataGrid-cell": {
                        fontSize: MobergFontSize.SMALL,
                        padding: "4px 8px",
                        minHeight: "28px !important",
                        maxHeight: "28px !important",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                    },
                    "& .MuiDataGrid-row": {
                        minHeight: "28px !important",
                        maxHeight: "28px !important",
                    },
                    "& .MuiDataGrid-columnHeaders": {
                        minHeight: "24px !important",
                        maxHeight: "24px !important",
                    },
                    "& .MuiDataGrid-columnHeader": {
                        minHeight: "24px !important",
                        maxHeight: "24px !important",
                        padding: "0px 4px !important",
                    },
                    "& .MuiDataGrid-columnHeaderTitle": {
                        fontSize: MobergFontSize.SMALL,
                        fontWeight: "bold",
                    },
                    "& .MuiDataGrid-footerContainer": {
                        minHeight: "32px !important",
                        maxHeight: "32px !important",
                        "& .MuiTablePagination-root": {
                            minHeight: "32px !important",
                        },
                        "& .MuiTablePagination-toolbar": {
                            minHeight: "32px !important",
                            padding: "0 8px",
                        },
                        "& .MuiTablePagination-displayedRows, & .MuiTablePagination-selectLabel": {
                            fontSize: "12px",
                            margin: "0",
                        },
                        "& .MuiIconButton-root": {
                            padding: "2px",
                        },
                    },
                }}
            />
        </MobergColumn>
    )
}